import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { ArrowRight, Calendar, Shield, CheckCircle, Clock, Sparkles } from 'lucide-react';
import SEO from '../components/SEO';

const CeramicCoating = () => {
  const benefits = [
    'Enhanced gloss and depth',
    'Hydrophobic water beading',
    'Easier washing and maintenance',
    'UV protection',
    'Chemical resistance',
    'Reduced light scratching from washing'
  ];

  const process = [
    { step: '1', title: 'Inspection', desc: 'Assess paint condition and discuss expectations' },
    { step: '2', title: 'Decontamination', desc: 'Remove embedded contaminants from the surface' },
    { step: '3', title: 'Paint Correction', desc: 'Remove defects as needed before coating' },
    { step: '4', title: 'Panel Prep', desc: 'Chemical and IPA wipe for proper bonding' },
    { step: '5', title: 'Coating Application', desc: 'Professional application with proper curing' },
    { step: '6', title: 'Curing & Care Instructions', desc: 'Guidance for the cure period and maintenance' }
  ];

  return (
    <div className="min-h-screen bg-[#111216]">
      <SEO 
        title="Ceramic Coating Simi Valley | Detailing Disciples"
        description="Professional ceramic coating with careful preparation and paint correction options in Simi Valley. Request a personalized vehicle quote."
        canonical="https://thedetailingdisciples.com/ceramic-coating"
      />

      {/* Hero */}
      <section className="pt-32 pb-16 bg-gradient-to-b from-[#1A1A1F] to-[#111216]">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center space-y-6">
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white">
              Ceramic Coating in <span className="text-[#B8860B]">Simi Valley</span>
            </h1>
            <p className="text-xl text-gray-300 leading-relaxed">
              Long-term gloss and easier maintenance begin with proper preparation. Your vehicle is washed, decontaminated, assessed, and corrected as needed before the coating is applied. Package recommendations are based on your vehicle's condition, use, and maintenance goals.
            </p>
          </div>
        </div>
      </section>

      {/* Who It's For */}
      <section className="py-16 bg-[#111216]">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-6 text-center">
              Best For
            </h2>
            <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-8">
              <ul className="space-y-4 text-gray-300">
                <li className="flex items-start">
                  <CheckCircle className="w-6 h-6 text-[#B8860B] mr-3 flex-shrink-0 mt-0.5" />
                  <span>Owners who plan to keep the vehicle and value long-term appearance</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="w-6 h-6 text-[#B8860B] mr-3 flex-shrink-0 mt-0.5" />
                  <span>Vehicles that deserve easier maintenance and consistent gloss</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="w-6 h-6 text-[#B8860B] mr-3 flex-shrink-0 mt-0.5" />
                  <span>Daily drivers, garage-kept vehicles, and special cars</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="w-6 h-6 text-[#B8860B] mr-3 flex-shrink-0 mt-0.5" />
                  <span>Owners willing to follow proper cure and maintenance procedures</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-16 bg-[#1A1A1F]">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-8 text-center">
              What Ceramic Coating <span className="text-[#B8860B]">Provides</span>
            </h2>
            <div className="grid md:grid-cols-2 gap-4">
              {benefits.map((benefit) => (
                <div key={benefit} className="bg-white/5 border border-white/10 rounded-xl p-4 flex items-center">
                  <Sparkles className="w-5 h-5 text-[#B8860B] mr-3 flex-shrink-0" />
                  <span className="text-gray-300">{benefit}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Process */}
      <section className="py-16 bg-[#111216]">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-8 text-center">
              The <span className="text-[#B8860B]">Process</span>
            </h2>
            <div className="space-y-4">
              {process.map((item) => (
                <div key={item.step} className="bg-white/5 border border-white/10 rounded-2xl p-6 flex items-start">
                  <div className="w-12 h-12 rounded-full bg-gradient-to-br from-[#B8860B] to-[#C8960B] flex items-center justify-center font-bold text-[#111216] mr-4 flex-shrink-0">
                    {item.step}
                  </div>
                  <div>
                    <h3 className="text-white font-bold text-lg mb-1">{item.title}</h3>
                    <p className="text-gray-400">{item.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* What It Cannot Do */}
      <section className="py-16 bg-[#1A1A1F]">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-6 text-center">
              What Ceramic Coating <span className="text-[#B8860B]">Cannot Do</span>
            </h2>
            <div className="bg-red-900/10 border border-red-900/30 rounded-2xl p-8">
              <ul className="space-y-3 text-gray-300">
                <li className="flex items-start">
                  <span className="text-red-500 mr-3 font-bold">✗</span>
                  <span>Make your vehicle maintenance-free or scratch-proof</span>
                </li>
                <li className="flex items-start">
                  <span className="text-red-500 mr-3 font-bold">✗</span>
                  <span>Prevent rock chips, deep scratches, or careless damage</span>
                </li>
                <li className="flex items-start">
                  <span className="text-red-500 mr-3 font-bold">✗</span>
                  <span>Eliminate the need for careful washing techniques</span>
                </li>
                <li className="flex items-start">
                  <span className="text-red-500 mr-3 font-bold">✗</span>
                  <span>Fix existing paint defects without proper correction first</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Timeframe */}
      <section className="py-16 bg-[#111216]">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
              Expected <span className="text-[#B8860B]">Timeframe</span>
            </h2>
            <div className="bg-white/5 border border-white/10 rounded-2xl p-8">
              <Clock className="w-12 h-12 text-[#B8860B] mx-auto mb-4" />
              <p className="text-gray-300 text-lg">
                Ceramic coating packages typically require 1-3 days depending on vehicle size, paint condition, and the level of correction needed before application.
              </p>
              <p className="text-[#B8860B] font-semibold mt-4">
                A 24-48 hour cure period follows before the vehicle can be washed or exposed to rain.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-gradient-to-r from-[#B8860B] to-[#C8960B]">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-[#111216] mb-4">
            Request a Ceramic Coating Quote
          </h2>
          <p className="text-[#111216]/80 text-lg mb-8 max-w-2xl mx-auto">
            Share your vehicle details and goals. We'll assess the paint and recommend the right package.
          </p>
          <Link to="/contact">
            <Button size="lg" className="bg-[#111216] hover:bg-[#1A1A1F] text-white font-bold">
              <Calendar className="w-5 h-5 mr-2" />
              Get Your Personalized Recommendation
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default CeramicCoating;